/**
 * The actions on one document row (Dokumente 2.0, S6).
 *
 * TWO ACTIONS, TWO WEIGHTS. Download is frequent and harmless, so it stays
 * a button a reader can hit without thinking. Delete is rare and permanent
 * — the row is hard-deleted and the stored file goes with it — so it moves
 * behind a `…` menu. Before S6 both sat side by side in every row, which
 * put a destructive action one mis-click from the common one, twenty-five
 * times per page, and made the action column the widest thing in the table
 * after the title itself.
 *
 * NOTHING ELSE MOVED INTO THE MENU. There is no second "Download" in it, no
 * rename, no copy-link, no share: a menu whose only purpose is to hold the
 * dangerous action stays honest about what it is for.
 *
 * ONE COMPONENT FOR BOTH VIEWPORTS. The table row and the mobile card render
 * this same block, so the two cannot drift into different action models —
 * which is exactly what S5 fixed for the row's information and S6 now does
 * for its behaviour.
 *
 * Shape and accessible name follow `ResponsibilityAreaList.RowActions`, the
 * repo's existing row-overflow pattern: icon-only trigger with an
 * `aria-label` that names the object, `align="end"` content, and the
 * destructive item carrying `text-destructive focus:text-destructive`
 * rather than a colour of its own.
 */
import React from "react";
import { MoreHorizontal } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";

export default function DocumentRowActions({ doc, onDownload, onDelete, busy }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        onClick={() => onDownload(doc)}
        disabled={busy}
        className="rounded-[8px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-primary hover:border-primary/40 disabled:opacity-50"
      >
        Download
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // The title is IN the name, not just the word "Aktionen": a
            // screen reader moving through twenty-five rows would otherwise
            // hear the same label twenty-five times and learn nothing.
            aria-label={`Aktionen für ${doc.title}`}
            disabled={busy}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {/* The colour is the token the theme owns, and the word "Löschen"
              carries the meaning on its own — the danger is never signalled
              by colour alone (§18/S6 §7). Selecting this only OPENS the
              confirmation; nothing is deleted until that is confirmed. */}
          <DropdownMenuItem
            onClick={() => onDelete(doc)}
            className="text-destructive focus:text-destructive"
          >
            Löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
